import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '@/api/client';
import { encodeReturnTo } from '@/lib/urlState';
import type { OfferDetail } from '@/lib/offers';

/**
 * Opens the offer form for one candidate — what picking "Offered" now does.
 *
 * Choosing Offered used to send a generic "we would like to offer you the role" email from
 * the stage dialog. An offer is now a letter with terms, so the pick creates (or reopens)
 * the offer and goes to its form instead; nothing reaches the candidate until HR previews
 * and sends it there. POST /offers returns the live offer when there already is one, so a
 * second pick, or a row's "Add", lands on the same draft rather than starting another.
 *
 * `ret` is the return ticket for the form's Back link. List pages pass their own, which has
 * the search text stripped; anywhere else it defaults to the current URL.
 */
export function useStartOffer() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  const m = useMutation({
    mutationFn: async (v: { invitationId: string; ret?: string }) =>
      (await api.post('/offers', { invitationId: v.invitationId })).data.data as OfferDetail,
    onSuccess: (offer, v) => {
      qc.invalidateQueries({ queryKey: ['offer', v.invitationId] });
      // The rows' badges read the latest offer: without these, Back lands on a list still
      // saying "No offer on file" for the five minutes a cached page is kept.
      qc.invalidateQueries({ queryKey: ['candidates-pipeline'] });
      qc.invalidateQueries({ queryKey: ['job-position'] });
      navigate(`/admin/offers/${offer.id}?ret=${v.ret ?? encodeReturnTo(location.pathname, location.search)}`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not open the offer form'),
  });

  return {
    startOffer: (invitationId: string, ret?: string) => m.mutate({ invitationId, ret }),
    starting: m.isPending,
  };
}

/** The offer form's address, carrying a return ticket back to where it was opened from. */
export function offerEditorHref(offerId: string, location: { pathname: string; search: string }): string {
  return `/admin/offers/${offerId}?ret=${encodeReturnTo(location.pathname, location.search)}`;
}
